import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Brain,
  CheckCircle,
  Clock,
  DollarSign,
  Sparkles,
  User,
  LogOut,
  Zap,
  Database,
  Shield,
  HelpCircle,
  Settings,
  Play,
  FileText,
  Users,
  Mail,
} from "lucide-react";
import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { FileUploadModal } from "./FileUploadModal";
import { AuthModal } from "./AuthModal";
import { useAuth } from "../lib/useAuth";
import { useNavigate } from "react-router-dom";

const AIBookLogo = () => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.8 }}
    className="relative"
  >
    <div className="absolute inset-0 rounded-2xl bg-cyber-blue opacity-20 blur-xl animate-pulse-glow"></div>
    <svg viewBox="0 0 120 120" className="w-56 h-56 mx-auto mb-6 relative z-10">
      <defs>
        <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--cyber-blue))" />
          <stop offset="50%" stopColor="hsl(var(--cyber-purple))" />
          <stop offset="100%" stopColor="hsl(var(--cyber-green))" />
        </linearGradient>
        <linearGradient id="bookGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--cyber-blue))" />
          <stop offset="100%" stopColor="hsl(var(--cyber-purple))" />
        </linearGradient>
      </defs>
      
      {/* Book base */}
      <rect x="20" y="30" width="80" height="60" rx="4" fill="url(#bookGradient)" className="drop-shadow-lg" />
      <rect x="22" y="32" width="76" height="56" rx="2" fill="rgba(255,255,255,0.1)" />
      
      {/* Book pages/lines */}
      <line x1="30" y1="42" x2="70" y2="42" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="50" x2="85" y2="50" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="58" x2="75" y2="58" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="66" x2="80" y2="66" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="74" x2="65" y2="74" stroke="white" strokeWidth="1.5" opacity="0.8" />
      
      {/* AI Brain overlay */}
      <circle cx="85" cy="45" r="22" fill="url(#logoGradient)" className="drop-shadow-lg" opacity="0.9" />
      <circle cx="85" cy="45" r="20" fill="rgba(255,255,255,0.1)" />
      
      {/* Neural network pattern */}
      <circle cx="78" cy="38" r="2" fill="white" opacity="0.9" />
      <circle cx="92" cy="40" r="2" fill="white" opacity="0.9" />
      <circle cx="80" cy="52" r="2" fill="white" opacity="0.9" />
      <circle cx="90" cy="50" r="2" fill="white" opacity="0.9" />
      <circle cx="85" cy="45" r="2" fill="white" />
      
      {/* Connecting lines for neural network */}
      <line x1="78" y1="38" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="92" y1="40" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="80" y1="52" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="90" y1="50" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      
      {/* Index symbol */}
      <text x="35" y="48" fill="white" fontSize="8" fontWeight="bold" opacity="0.7">A</text>
      <text x="40" y="56" fill="white" fontSize="6" opacity="0.7">B</text>
      <text x="45" y="64" fill="white" fontSize="6" opacity="0.7">C</text>
    </svg>
  </motion.div>
);

const FeatureCard = ({
  icon,
  title,
  description,
  delay = 0,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}) => (
  <motion.div
    initial={{ y: 20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay, duration: 0.8 }}
    whileHover={{ y: -5, scale: 1.02 }}
    className="group"
  >
    <Card className="glass-card hover:glow-blue transition-all duration-700 border-border/50 backdrop-blur-md h-full">
      <CardHeader>
        <div className="text-cyber-blue mb-4 bg-cyber-blue/10 w-12 h-12 rounded-xl flex items-center justify-center ring-1 ring-cyber-blue/30 group-hover:ring-cyber-blue/60 transition-all duration-500 group-hover:bg-cyber-blue/20">
          {icon}
        </div>
        <CardTitle className="text-xl text-foreground font-bold tracking-tight">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  </motion.div>
);

const TestimonialCard = ({
  quote,
  author,
  role,
  delay = 0,
}: {
  quote: string;
  author: string;
  role: string;
  delay?: number;
}) => (
  <motion.div
    initial={{ scale: 0.95, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay, duration: 0.8 }}
    whileHover={{ scale: 1.02 }}
  >
    <Card className="glass-card border-border/50 shadow-cyber backdrop-blur-md">
      <CardContent className="pt-6">
        <p className="text-muted-foreground italic mb-4 leading-relaxed text-base">{quote}</p>
        <div className="font-semibold text-foreground text-lg">{author}</div>
        <div className="text-sm text-muted-foreground">{role}</div>
      </CardContent>
    </Card>
  </motion.div>
);

const MetricBadge = ({ 
  icon, 
  label, 
  delay = 0 
}: { 
  icon: React.ReactNode; 
  label: string; 
  delay?: number;
}) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay, duration: 0.8 }}
    whileHover={{ scale: 1.05 }}
    className="flex items-center gap-2 sm:gap-3 glass-card px-3 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 rounded-full border-border/50 hover:border-cyber-blue/50 transition-all duration-500 hover:glow-blue"
  >
    <div className="text-cyber-blue flex-shrink-0">{React.cloneElement(icon as React.ReactElement, { size: 16, className: "sm:w-5 sm:h-5" })}</div>
    <span className="text-foreground font-medium text-xs sm:text-sm tracking-wide whitespace-nowrap">{label}</span>
  </motion.div>
);

const InfoTab = ({
  icon,
  title,
  isActive,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 rounded-lg sm:rounded-xl transition-all duration-500 w-full sm:w-auto justify-center sm:justify-start ${
      isActive
        ? 'glass-card border-cyber-blue/50 text-cyber-blue glow-blue'
        : 'border border-border/30 text-muted-foreground hover:text-foreground hover:border-border/50'
    }`}
  >
    <div className={`p-1 sm:p-2 rounded-md sm:rounded-lg transition-all duration-300 flex-shrink-0 ${
      isActive ? 'bg-cyber-blue/20 text-cyber-blue' : 'bg-muted/20'
    }`}>
      {React.cloneElement(icon as React.ReactElement, { size: 16, className: "sm:w-5 sm:h-5" })}
    </div>
    <span className="font-medium text-sm sm:text-base">{title}</span>
  </button>
);

export const LandingPage: React.FC = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'who' | 'how' | 'faq'>('who');
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      setIsUploadModalOpen(true);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">

      {/* Header with User Info or Login - Mobile Responsive */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        {isAuthenticated ? (
          <div className="flex items-center gap-2 sm:gap-4 glass-card px-3 py-2 sm:px-6 sm:py-3 rounded-full border-border/50 backdrop-blur-md">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
              className="text-muted-foreground hover:text-cyber-blue transition-colors text-xs sm:text-sm px-2 sm:px-3"
            >
              <span className="hidden sm:inline">My Indexes</span>
              <span className="sm:hidden">Projects</span>
            </Button>
            <div className="flex items-center gap-1 sm:gap-2">
              <User size={14} className="text-cyber-blue sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium text-foreground max-w-[80px] sm:max-w-none truncate">{user?.name || user?.email}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-muted-foreground hover:text-destructive transition-colors"
            >
              <LogOut size={14} className="sm:w-4 sm:h-4" />
            </Button>
          </div>
        ) : (
          <div className="glass-card px-3 py-2 sm:px-6 sm:py-3 rounded-full border-border/50 backdrop-blur-md">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAuthModalOpen(true)}
              className="text-muted-foreground hover:text-cyber-blue transition-colors text-xs sm:text-sm"
            >
              <User size={14} className="mr-1 sm:mr-2 sm:w-4 sm:h-4" />
              Login
            </Button>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 sm:py-12 md:py-16 relative flex flex-col items-center justify-center">
        {/* Hero Section */}
        <div className="text-center mb-12 sm:mb-16 md:mb-24">
          <AIBookLogo />
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-cyber-blue mb-3 sm:mb-4 tracking-wide leading-tight relative">
              <span className="relative z-10">AI-indexing</span>
              <div className="absolute inset-0 text-cyber-purple opacity-30 blur-sm">AI-indexing</div>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-10 md:mb-12 max-w-3xl mx-auto leading-relaxed font-medium px-2 sm:px-0">
              Professional indexing enhanced by secure, intelligent automation. Empowering indexers with advanced tools 
              while preserving human expertise, decision-making, and complete data ownership.
            </p>
          </motion.div>
          
          {/* Metrics */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-12 md:mb-16 px-2"
          >
            <MetricBadge icon={<Brain size={20} />} label="Intelligent" delay={0.6} />
            <MetricBadge icon={<Zap size={20} />} label="Accelerated" delay={0.7} />
            <MetricBadge icon={<Shield size={20} />} label="Secure & Private" delay={0.8} />
            <MetricBadge icon={<BookOpen size={20} />} label="Cindex Compatible*" delay={0.9} />
          </motion.div>
          
          {/* CTA Button */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.8 }}
          >
            <Button
              size="lg"
              className="btn-cyber text-sm sm:text-base md:text-lg px-6 py-4 sm:px-8 sm:py-6 md:px-12 md:py-8 h-auto shadow-cyber hover:shadow-glow-lg transition-all duration-500 rounded-xl sm:rounded-2xl text-background font-bold tracking-wide group w-full sm:w-auto max-w-sm sm:max-w-none"
              onClick={handleGetStarted}
            >
              <span className="inline-flex items-center justify-center">
                <span className="hidden sm:inline">{isAuthenticated ? 'Index New Book' : 'Index Your Book Today'}</span>
                <span className="sm:hidden">{isAuthenticated ? 'Index New Book' : 'Get Started'}</span>
                <ArrowRight
                  className="ml-2 sm:ml-3 group-hover:translate-x-1 transition-transform duration-300"
                  size={20}
                />
              </span>
            </Button>
          </motion.div>
        </div>

        {/* How It Works / FAQ Section */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="w-full mb-16 sm:mb-24 md:mb-32"
        >
          <div className="text-center mb-8 sm:mb-10 md:mb-12 px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 tracking-tight">
              Learn More
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
              Understand how our intelligent indexing system works and get answers to common questions.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mb-6 sm:mb-8 px-4">
            <InfoTab
              icon={<BookOpen size={20} />}
              title="Who's it for?"
              isActive={activeTab === 'who'}
              onClick={() => setActiveTab('who')}
            />
            <InfoTab
              icon={<Settings size={20} />}
              title="How It Works"
              isActive={activeTab === 'how'}
              onClick={() => setActiveTab('how')}
            />
            <InfoTab
              icon={<HelpCircle size={20} />}
              title="FAQ"
              isActive={activeTab === 'faq'}
              onClick={() => setActiveTab('faq')}
            />
            {/* <InfoTab
              icon={<User size={20} />}
              title="Testimonials"
              isActive={activeTab === 'testimonials'}
              onClick={() => setActiveTab('testimonials')}
            /> */}
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 border-border/50 backdrop-blur-md mx-auto"
          >
            {activeTab === 'who' ? (
              <div className="space-y-6 sm:space-y-8 md:space-y-12">
                <div className="text-center">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-4 sm:mb-6">
                    Who's it for?
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-10 md:mb-12">
                    An index is the detailed roadmap at the back of a book or document that helps readers instantly find the people, places, and ideas they care about. If your work is long, information-dense, or meant to be revisited, an index makes it dramatically more usable and valuable.
                  </p>
                  
                  <p className="text-base sm:text-lg md:text-xl font-semibold text-foreground mb-8 sm:mb-10 md:mb-12">
                    That means it's for:
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10 max-w-5xl mx-auto">
                  {/* Authors & Publishers */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="glass-card p-6 sm:p-8 rounded-2xl border-border/30 bg-background/20 hover:bg-background/30 transition-all duration-300 hover:shadow-glow-sm"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-cyber-blue/10 p-3 rounded-full flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-cyber-blue" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                          Authors & Publishers
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          of nonfiction, biographies, anthologies, textbooks, and reference works
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Scholars & Students */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="glass-card p-6 sm:p-8 rounded-2xl border-border/30 bg-background/20 hover:bg-background/30 transition-all duration-300 hover:shadow-glow-sm"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-cyber-purple/10 p-3 rounded-full flex-shrink-0">
                        <User className="w-6 h-6 text-cyber-purple" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                          Scholars & Students
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          writing dissertations, theses, monographs, or edited volumes
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Professionals */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="glass-card p-6 sm:p-8 rounded-2xl border-border/30 bg-background/20 hover:bg-background/30 transition-all duration-300 hover:shadow-glow-sm"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-cyber-green/10 p-3 rounded-full flex-shrink-0">
                        <Settings className="w-6 h-6 text-cyber-green" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                          Professional Indexers
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          seeking to accelerate their workflow while maintaining the quality and expertise that clients expect
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Specialty Writers */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="glass-card p-6 sm:p-8 rounded-2xl border-border/30 bg-background/20 hover:bg-background/30 transition-all duration-300 hover:shadow-glow-sm"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-cyber-blue/10 to-cyber-purple/10 p-3 rounded-full flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-cyber-blue" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                          Specialty Writers
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          creating cookbooks, religious texts, or catalogs
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            ) : activeTab === 'how' ? (
              <div className="space-y-6 sm:space-y-8 md:space-y-12">
                <div className="text-center">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-4 sm:mb-6">
                    How Our System Assists You
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                    Creating a good index is more than just collecting and sorting words—it requires professional judgment, subject expertise, 
                    and understanding of how readers think. Our secure system handles the time-consuming groundwork while you make the 
                    critical decisions that transform a word list into a truly useful index.
                  </p>
                </div>

                {/* Content Area for Your Paragraphs */}
                <div className="glass-card p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border-border/30 bg-background/20">
                  <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-4 sm:mb-6 text-center">
                    The Process
                  </h4>
                  <div className="space-y-4 sm:space-y-6 text-muted-foreground leading-relaxed">
                    <ol className="list-decimal list-inside space-y-2 sm:space-y-3 text-sm sm:text-base md:text-lg">
                      <li>
                        <span className="text-foreground font-medium">Upload & configure.</span> Add your PDF document, set your start page, and (optionally) mark where endnotes/footnotes begin and end. This keeps page references precise.
                      </li>
                      <li>
                        <span className="text-foreground font-medium">Secure term collection.</span> The system securely scans the manuscript, extracts candidate terms, and condenses nearby context into short, human-readable summaries.
                      </li>
                      <li>
                        <span className="text-foreground font-medium">Intelligent suggestions.</span> The system proposes potential duplicates to merge, cross‑references to add, and subentries to consider. Every decision remains yours—accept, modify, or reject as your expertise guides.
                      </li>
                      <li>
                        <span className="text-foreground font-medium">Human-in-the-loop review.</span> At each stage you can merge terms, add cross‑references, and assign subentries. Changes persist and flow forward automatically.
                      </li>
                      <li>
                        <span className="text-foreground font-medium">Finalize & export.</span> Generate a clean, publisher‑grade index with grouped entries, subentries, page ranges, and cross‑references. Export to DOCX and Cindex‑compatible XML.
                      </li>
                    </ol>
                  </div>
                </div>

                {/* Demo Video Section */}
                <div className="glass-card p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border-border/30 bg-background/20">
                  <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-4 sm:mb-6 text-center">
                    See It In Action
                  </h4>
                  <div className="relative aspect-video rounded-lg sm:rounded-xl overflow-hidden border border-border/50 bg-black/60">
                    <iframe
                      className="w-full h-full"
                      src="https://www.youtube.com/embed/Mak_1lEs-fQ"
                      title="AI Indexing Demo"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                {/* Features Grid */}
                <div>
                  <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-6 sm:mb-8 text-center">
                    Key Features
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <FeatureCard
            icon={<Clock size={24} />}
                      title="Hours, Not Weeks"
            description="Generate comprehensive indexes in minutes. Save weeks of manual work and meet your deadlines with confidence."
            delay={0.2}
          />
          <FeatureCard
            icon={<Brain size={24} />}
            title="Academic Intelligence"
            description="Purpose-built for academic publishing, our system recognizes complex subject matter and technical terminology."
            delay={0.4}
          />
          <FeatureCard
            icon={<BookOpen size={24} />}
            title="Publisher Standards"
            description="Automatically follows major academic publisher style guides while maintaining professional consistency."
            delay={0.6}
          />
                    <FeatureCard
                      icon={<Database size={24} />}
                      title="Smart Recognition"
                      description="Advanced pattern recognition identifies key concepts, entities, and relationships automatically."
                      delay={0.8}
                    />
                    <FeatureCard
                      icon={<Shield size={24} />}
                      title="Data Ownership & Privacy"
                      description="You own your data completely. We don't train on your content, and processing uses encrypted, secure connections."
                      delay={1.2}
                    />
                    <FeatureCard
                      icon={<BookOpen size={24} />}
                      title="Cindex Compatible*"
                      description="Export clean XML that imports directly into Cindex, preserving entries, subentries, page ranges, and cross‑references."
                      delay={1.4}
                    />
                  </div>
                </div>
              </div>
            ) : activeTab === 'faq' ? (
              <div className="space-y-6 sm:space-y-8">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6 text-center">
                  Frequently Asked Questions
                </h3>
                <div className="text-muted-foreground leading-relaxed space-y-4 sm:space-y-6">
                  <div className="grid gap-4 sm:gap-6">
                    <div className="border border-border/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <Brain className="w-4 h-4 mr-2 text-cyber-blue" />
                        Does this software use AI?
                      </h4>
                      <p className="text-xs sm:text-sm mb-3"><strong>Yes, transparently.</strong> Here's exactly how we use AI technology:</p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc">
                        <li><strong>GPT-4o via API:</strong> We use OpenAI's GPT-4o model through their official API</li>
                        <li><strong>Specific tasks:</strong> Term extraction, context analysis, and relationship suggestions</li>
                        <li><strong>Human oversight:</strong> All AI suggestions require your professional review and approval</li>
                        <li><strong>No training:</strong> Your data is not used to train or improve AI models</li>
                        <li><strong>Tool, not replacement:</strong> AI handles tedious work so you can focus on expert decisions</li>
                      </ul>
                    </div>
                    <div className="border border-cyber-blue/20 bg-cyber-blue/5 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <Shield className="w-4 h-4 mr-2 text-cyber-blue" />
                        Is my manuscript data secure and private?
                      </h4>
                      <p className="text-xs sm:text-sm mb-3"><strong>You own and control your data completely.</strong> We use enterprise-grade security:</p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc">
                        <li><strong>No training:</strong> OpenAI and other providers do not train their models on your business data by default</li>
                        <li><strong>Encrypted processing:</strong> All data is encrypted in transit (TLS 1.2+) and at rest (AES-256)</li>
                        <li><strong>Your ownership:</strong> You own your inputs and outputs—we never share your content with third parties</li>
                        <li><strong>Data retention control:</strong> Delete your projects anytime, and we'll remove all associated data</li>
                        <li><strong>Minimal exposure:</strong> We only send the specific text segments needed for each processing step</li>
                      </ul>
                    </div>
                    <div className="border border-border/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-cyber-purple" />
                        What files can I upload?
                      </h4>
                      <p className="text-xs sm:text-sm"><strong>PDF documents only</strong> are supported. During upload, you can configure:</p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc mt-2">
                        <li>Start page number for accurate page references</li>
                        <li>Endnotes/footnotes location for proper citation handling</li>
                        <li>Page range settings to ensure precise indexing</li>
                      </ul>
                    </div>
                    <div className="border border-border/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <Users className="w-4 h-4 mr-2 text-cyber-green" />
                        Can this tool index my book with no intervention?
                      </h4>
                      <p className="text-xs sm:text-sm mb-3"><strong>Professional oversight is essential.</strong> The system can process automatically, but it's designed for collaboration:</p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc">
                        <li><strong>System handles:</strong> Initial scanning, term extraction, and organization</li>
                        <li><strong>You decide:</strong> Merges, cross‑references, subentries, and final structure</li>
                        <li><strong>Result:</strong> Human expertise enhanced by computational efficiency</li>
                      </ul>
                    </div>
                    <div className="border border-border/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <DollarSign className="w-4 h-4 mr-2 text-cyber-blue" />
                        What does it cost?
                      </h4>
                      <p className="text-xs sm:text-sm mb-3"><strong>Simple, transparent pricing:</strong></p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc">
                        <li><strong>20 free pages</strong> to get started and test the system</li>
                        <li><strong>$0.10 per page</strong> after trial pages are used</li>
                        <li><strong>No monthly fees</strong> or hidden charges</li>
                        <li><strong>Pay only</strong> for what you process</li>
                      </ul>
                    </div>
                    <div className="border border-border/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-cyber-purple" />
                        How long does it take?
                      </h4>
                      <p className="text-xs sm:text-sm mb-3"><strong>Work at your own pace:</strong></p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc">
                        <li><strong>Initial draft:</strong> System produces suggestions in minutes</li>
                        <li><strong>Review phase:</strong> Take as long as you need for quality decisions</li>
                        <li><strong>Professional control:</strong> Refine at your own pace for publisher‑grade results</li>
                        <li><strong>Time savings:</strong> Focus on expertise, not manual data entry</li>
                      </ul>
                    </div>
                    <div className="border border-border/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
                      <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-cyber-green" />
                        Want new features?
                      </h4>
                      <p className="text-xs sm:text-sm mb-3"><strong>We value your feedback and suggestions:</strong></p>
                      <ul className="text-xs sm:text-sm space-y-1 ml-4 list-disc">
                        <li><strong>Feature requests:</strong> Tell us what would make your workflow better</li>
                        <li><strong>Professional input:</strong> Share insights from your indexing experience</li>
                        <li><strong>Contact us:</strong> <a className="text-cyber-blue underline font-medium" href="mailto:ai.indexing@gmail.com">ai.indexing@gmail.com</a></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Testimonials section temporarily removed */
              <div className="text-center py-12">
                <p className="text-muted-foreground">More information coming soon!</p>
              </div>
            )}
              </motion.div>
        </motion.div>

        {/* Footer with Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="w-full mt-16 sm:mt-24 md:mt-32 pt-8 sm:pt-12 border-t border-border/30"
        >
          <div className="text-center">
            <p className="text-xs sm:text-sm text-muted-foreground/80 leading-relaxed">
              *AI-indexing is not affiliated with, sanctioned by, or endorsed by OpenCindex LLC, 
              the developers and distributors of Cindex indexing software.
            </p>
          </div>
        </motion.div>

      </div>

      <FileUploadModal 
        open={isUploadModalOpen} 
        onOpenChange={setIsUploadModalOpen} 
      />

      <AuthModal 
        open={isAuthModalOpen} 
        onOpenChange={setIsAuthModalOpen} 
        defaultMode="login"
      />
    </div>
  );
};
